// src/components/generic-crud/GenericTableRow.js
import PropTypes from 'prop-types';
import {
  Button,
  Avatar,
  Tooltip,
  MenuItem,
  TableRow,
  Checkbox,
  TableCell,
  IconButton,
  ListItemText,
  Switch,
  Box,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useBoolean } from 'src/hooks/use-boolean';
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import SvgColor from 'src/components/svg-color';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { fDate, fDateTime } from 'src/utils/format-time';

export default function GenericTableRow({
  row,
  selected,
  onEditRow,
  onDuplicateRow,
  onSelectRow,
  onDeleteRow,
  config,
  visibleColumns = {},
  columnOrder = [],
  features = {},
  currentUserId,
  currentUser,
  rowIndex,
  limitReached,
}) {
  const confirm = useBoolean();
  const confirmSwitch = useBoolean();
  const [pendingToggle, setPendingToggle] = useState(null); // { checked, column }
  const popover = usePopover();

  // 🛡️ Calculate Action Visibility
  const getIsOwner = (rowData) => {
    const owner = rowData.createdBy || rowData.userId || rowData.expense_by || rowData.author;
    const ownerId = typeof owner === 'object' ? (owner?._id || owner?.id) : owner;
    
    // Check against both primary ID (adminRef) and orgUserId for moderators/admins
    return ownerId === currentUserId || (currentUser?.orgUserId && ownerId === currentUser.orgUserId);
  };

  const isOwner = getIsOwner(row);

  const editEnabled = typeof features.edit === 'function' ? features.edit(row) : features.edit !== false;
  const deleteEnabled = typeof features.delete === 'function' ? features.delete(row) : features.delete !== false;

  const canEdit = !config.actions && editEnabled && (features.isAdmin || features.editAll || (features.editSelf && isOwner));
  
  const canDelete = !config.table?.actions && deleteEnabled && (features.isAdmin || features.deleteAll || (features.deleteSelf && isOwner));
  
  const canDuplicate = !config.actions && features.duplicate === true && !limitReached;

  const visibleCustomActions = (features.customActions || []).filter(action => {
    if (action.condition && !action.condition(row)) return false;
    return true;
  });

  const visibleLegacyActions = (config.actions && typeof config.actions === 'function') 
    ? config.actions(row).filter(action => {
        if (action.show !== undefined) {
          return typeof action.show === 'function' ? action.show(row) : action.show;
        }
        return true;
      })
    : [];

  const anyActionVisible = canEdit || canDelete || canDuplicate || visibleCustomActions.length > 0 || visibleLegacyActions.length > 0;

  // Render cell content based on column type
  const renderCellContent = (column) => {
    const value = row[column.id];

    // Use custom render function if provided
    if (column.render && typeof column.render === 'function') {
      const rendered = column.render(value, row, { ...config, features }); // Merge features into config for render function

      // If render returns switch object, display as Switch
      if (rendered && typeof rendered === 'object' && rendered.type === 'switch') {
        // Support both old (config.*) and newer (config.table.*) placements
        const togglingRows = config.togglingRows || config.table?.togglingRows;
        const onToggleApprove = config.onToggleApprove || config.table?.onToggleApprove;

        // Check if this row is currently being toggled
        const isToggling = togglingRows && togglingRows.has(row._id);

        return (
          <Switch
            checked={rendered.checked}
            disabled={rendered.disabled || isToggling}
            color={rendered.color || 'success'}
            onChange={(event, checked) => {
              if (onToggleApprove && !isToggling) {
                // If column defines a confirm dialog and we're turning ON, show it first
                if (checked && column.confirmToggle) {
                  setPendingToggle({ checked, column });
                  confirmSwitch.onTrue();
                } else {
                  onToggleApprove(row, checked);
                }
              }
            }}
          />
        );
      }

      // If render returns iconPreview object, display icon with label
      if (rendered && typeof rendered === 'object' && rendered.type === 'iconPreview') {
        const iconPath = rendered.isNavbar ? `/assets/icons/navbar/${rendered.icon}.svg` : null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {rendered.isNavbar ? (
              <SvgColor src={iconPath} sx={{ width: 24, height: 24 }} />
            ) : (
              <Iconify icon={rendered.icon} width={24} />
            )}
            <span>{rendered.icon}</span>
          </div>
        );
      }

      // If render returns status object, display as Label
      if (rendered && typeof rendered === 'object' && 'label' in rendered) {
        return (
          <Label variant="soft" color={rendered.color || 'default'}>
            {rendered.label}
          </Label>
        );
      }

      return rendered;
    }

    // Handle different column types
    switch (column.type) {
      case 'status':
        // Fallback status rendering if no custom render function
        return (
          <Label variant="soft" color={value ? 'success' : 'error'}>
            {value ? 'Active' : 'Inactive'}
          </Label>
        );

      case 'date':
        return value ? fDate(value, column.format || 'dd MMM yyyy') : '-';

      case 'datetime':
        return value ? fDateTime(value, column.format || 'dd MMM yyyy HH:mm') : '-';

      case 'boolean':
        return (
          <Label variant="soft" color={value ? 'success' : 'default'}>
            {value ? 'Yes' : 'No'}
          </Label>
        );

      case 'avatar':
        return <Avatar alt={row.name || 'Avatar'} src={value} sx={{ width: 40, height: 40 }} />;

      case 'badge':
        return (
          <Label variant="soft" color="primary">
            {value}
          </Label>
        );

      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : '-';

      case 'currency':
        return typeof value === 'number'
          ? new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: column.currency || 'USD',
          }).format(value)
          : '-';

      case 'text':
      default:
        return value ?? '-';
    }
  };

  // Build column components dynamically
  const columnComponents = columnOrder.reduce((acc, columnId) => {
    const column = (config.table?.columns || config.columns).find((col) => col.id === columnId);

    if (!column || !visibleColumns[columnId]) {
      return acc;
    }

    acc[columnId] = (
      <TableCell key={columnId} align={column.align || 'left'}>
        {renderCellContent(column)}
      </TableCell>
    );

    return acc;
  }, {});

  // Checkbox column is shown only when selection is enabled
  const showCheckbox = features.select !== false && features.isSelectable !== false;

  return (
    <>
      <TableRow hover selected={selected}>
        {showCheckbox && (
          <TableCell padding="checkbox">
            <Checkbox 
              checked={selected} 
              onClick={() => onSelectRow(row.id)} 
              disabled={features.isSelectable && !features.isSelectable(row)}
            />
          </TableCell>
        )}

        <TableCell align="center">
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {rowIndex}
          </Typography>
        </TableCell>

        {/* Render visible columns in order */}
        {columnOrder.map((columnId) => columnComponents[columnId])}

        {/* Actions column */}
        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          {anyActionVisible ? (
            <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
              <Iconify icon="eva:more-vertical-fill" />
            </IconButton>
          ) : (
            <Box sx={{ pr: 1.5, color: 'text.disabled' }}>-</Box>
          )}
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
      sx={{ width: 180 }}
      >
        {/* Features-based Custom Actions (from config.features.customActions) */}
        {features.customActions &&
          features.customActions.map((action, index) => {
            // Check condition or hidden if provided
            if (action.condition && !action.condition(row)) {
              return null;
            }
            if (action.hidden && (typeof action.hidden === 'function' ? action.hidden(row) : action.hidden)) {
              return null;
            }

            let iconName = action.icon;
            if (iconName === 'check') {
              iconName = 'solar:check-circle-bold';
            } else if (iconName === 'close') {
              iconName = 'solar:close-circle-bold';
            }

            return (
              <MenuItem
                key={`custom-action-${index}`}
                onClick={() => {
                  console.log('🔘 Custom action clicked:', action.label);

                  if (typeof action.onClick === 'function') {
                    // Prioritize dedicated onClick handler if provided
                    action.onClick(row);
                  } else if (typeof action.action === 'function') {
                    // Direct function execution
                    action.action(row);
                  } else if (config.onCustomAction) {
                    // String identifier execution via parent config
                    config.onCustomAction(action.action, row, {
                      onEdit: () => {
                        onEditRow(row.id);
                      },
                      onDelete: () => {
                        onDeleteRow(row.id);
                      },
                    });
                  }
                  popover.onClose();
                }}
                sx={{ color: action.color ? `${action.color}.main` : 'inherit' }}
              >
                {action.icon && <Iconify icon={iconName} />}
                {action.label}
              </MenuItem>
            );
          })}

        {/* Custom Row Actions (Legacy/Direct Function) */}
        {config.actions && typeof config.actions === 'function' && (
          <>
            {(() => {
              const actions = config.actions(row);
              return actions.map((action, index) => {
                // Determine if action should be shown
                let shouldShow = true;
                if (action.show !== undefined) {
                  if (typeof action.show === 'function') {
                    shouldShow = action.show(row);
                  } else {
                    shouldShow = action.show;
                  }
                }

                if (!shouldShow) {
                  return null;
                }

                return (
                  <MenuItem
                    key={index}
                    onClick={() => {
                      action.onClick(row);
                      popover.onClose();
                    }}
                    sx={action.color ? { color: `${action.color}.main` } : {}}
                  >
                    {action.icon && <Iconify icon={action.icon} />}
                    {action.label}
                  </MenuItem>
                );
              });
            })()}
          </>
        )}

        {/* Default Edit/Delete Actions (only if no custom actions) */}
        {!config.actions && canEdit && (
          <MenuItem
            onClick={() => {
              onEditRow(row.id);
              popover.onClose();
            }}
          >
            <Iconify icon="solar:pen-bold" />
            Edit
          </MenuItem>
        )}

        {!config.actions && features.duplicate === true && !limitReached && (
          <MenuItem
            onClick={() => {
              onDuplicateRow();
              popover.onClose();
            }}
          >
            <Iconify icon="solar:copy-bold" />
            Duplicate
          </MenuItem>
        )}

        {!config.table?.actions && canDelete && (
          <MenuItem
            onClick={() => {
              confirm.onTrue();
              popover.onClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <Iconify icon="solar:trash-bin-trash-bold" />
            Delete
          </MenuItem>
        )}
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete"
        content="Are you sure you want to delete this item?"
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              onDeleteRow();
              confirm.onFalse();
            }}
          >
            Delete
          </Button>
        }
      />

      {/* Switch-specific confirm dialog (for confirmToggle columns) */}
      <ConfirmDialog
        open={confirmSwitch.value}
        onClose={() => {
          confirmSwitch.onFalse();
          setPendingToggle(null);
        }}
        title={pendingToggle?.column?.confirmToggle?.title || 'Confirm'}
        content={typeof pendingToggle?.column?.confirmToggle?.content === 'function'
          ? pendingToggle.column.confirmToggle.content(row)
          : pendingToggle?.column?.confirmToggle?.content || 'Are you sure?'}
        action={
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              confirmSwitch.onFalse();
              const onToggleApprove = config.onToggleApprove || config.table?.onToggleApprove;
              if (onToggleApprove && pendingToggle) {
                onToggleApprove(row, pendingToggle.checked);
              }
              setPendingToggle(null);
            }}
          >
            Enable
          </Button>
        }
      />
    </>
  );
}

GenericTableRow.propTypes = {
  row: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onEditRow: PropTypes.func,
  onDuplicateRow: PropTypes.func,
  onSelectRow: PropTypes.func,
  onDeleteRow: PropTypes.func,
  config: PropTypes.object.isRequired,
  visibleColumns: PropTypes.object,
  columnOrder: PropTypes.array,
  features: PropTypes.object,
  currentUserId: PropTypes.string,
  currentUser: PropTypes.object,
  rowIndex: PropTypes.number,
  limitReached: PropTypes.bool,
};
